import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import FAQSection, { faqData } from "@/components/FAQSection";
import { Helmet } from "react-helmet";
import blogPpfVsCeramic from "@/assets/blog-ppf-vs-ceramic.jpg";
import blogLeasedCarPpf from "@/assets/blog-leased-car-ppf.jpg";
import blogInstallerMistakes from "@/assets/blog-installer-mistakes.jpg";

const blogPosts = [
  {
    slug: "ppf-vs-ceramic-coating-nevada-sun",
    title: "PPF vs. Ceramic Coating: An Expert Guide for the Nevada Sun",
    excerpt: "Discover which protection solution is right for your vehicle in the harsh Las Vegas climate. We break down the key differences and help you make an informed decision.",
    image: blogPpfVsCeramic,
    date: "2024-01-15",
    category: "PPF"
  },
  {
    slug: "ppf-worth-it-leased-car-las-vegas",
    title: "Is Paint Protection Film Worth It on a Leased Car in Las Vegas?",
    excerpt: "Learn why PPF is a smart investment for leased vehicles in Las Vegas. Protect your deposit and maintain pristine condition throughout your lease term.",
    image: blogLeasedCarPpf,
    date: "2024-01-10",
    category: "PPF"
  },
  {
    slug: "5-mistakes-choosing-ppf-installer-las-vegas",
    title: "The 5 Mistakes to Avoid When Choosing a PPF Installer in Las Vegas",
    excerpt: "Not all installers are created equal. Discover the critical factors that separate professional installations from costly mistakes.",
    image: blogInstallerMistakes,
    date: "2024-01-05",
    category: "Installation"
  }
];

const Resources = () => {
  // Generate FAQ schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.flatMap(category => 
      category.questions.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    )
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Car Care Resources & FAQs | Battle Born PPF Las Vegas</title>
        <meta name="description" content="Expert guides on PPF, ceramic coating & window tint in Las Vegas. FAQs answered by XPEL certified professionals. Learn how to protect your vehicle." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-darker-bg to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              Resources & Insights
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Expert guides and insights on automotive protection in Las Vegas
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {blogPosts.map((post) => (
              <Card key={post.slug} className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <h3 className="text-xl font-heading font-bold mb-3 group-hover:text-accent transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-muted-foreground mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Button asChild variant="premium" className="w-full">
                    <Link to={`/resources/${post.slug}`}>Read More</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Ready to Protect Your Vehicle?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Get expert advice and a free quote for your automotive protection needs
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Resources;
